import { FileUpload } from '@roc/ui';
import { observer } from 'mobx-react';
import { Document as DocumentType } from '@roc/feature-types';
import { UNDERWRITING } from '@roc/feature-utils';
import { Box, CircularProgress, Grid, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { useDocumentStore } from '@roc/feature-documents';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import { Error } from '@material-ui/icons';

interface PreviewDocumentProps {
  document: DocumentType;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    document: {
      width: 'fit-content',
      margin: '0 auto',
    },
    page: {
      margin: theme.spacing(2),
      boxShadow: '2px 2px 4px #888',
      width: 'fit-content',
    },
    chatContainer: {
      flex: 1,
      height: ''
    },
    createChatButton: {
      letterSpacing: 'normal',
      textTransform: 'none',
      fontWeight: 'normal',
      fontSize: '16px',
      backgroundColor: '#4285F4',
      width: '307px',
      height: '64px',
      padding: '8px 16px',
      boxShadow: '0px 4px 4px 0px #00000040',
      '&:hover': {
        backgroundColor: '#357AE8',
      },
    },
    createChatButtonContainer: {
      height: '75vh',
      border: '1px solid #CBCBCB',
      borderRadius: '10px',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column'
    },
    circleIconContainer: {
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '5px',
    }
  })
);

export const PreviewDocument = observer(({ document }: PreviewDocumentProps) => {
  const classes = useStyles();
  const { documentStore } = useDocumentStore();
  const [scale, setScale] = useState(1.0);
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const [numPages, setNumPages] = useState(null);
  const [showDocumentLoading, setShowDocumentLoading] = useState<boolean>(false);
  const [isPdfFile, setIsPdfFile] = useState<boolean>(false);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  useEffect(() => {
    syncUploads(document)
    checkIfPdf()
    if (document.canDownloadFile) {
      documentStore.previewDocumentV2(document);
    }
  }, [])

  useEffect(() => {
    if (uploads[0] && uploads[0].file != null) {
      setShowDocumentLoading(true);
      documentStore.uploadDocument(uploads[0].file, document, UNDERWRITING, () => {
        documentStore.previewDocumentV2(document);
        setShowDocumentLoading(false);
        checkIfPdf();
      });
    }
  }, [uploads])



  const checkIfPdf = () => {
    if (uploads[0]) {
      if (uploads[0].name.split('.')[1] === 'pdf') {
        setIsPdfFile(true)
      } else {
        setIsPdfFile(false)
      }
    } else {
      if (document.docType === 'pdf') {
        setIsPdfFile(true)
      } else {
        setIsPdfFile(false)
      }
    }
  }

  const syncUploads = (newDoc: DocumentType) => {
    if (newDoc.canDownloadFile) {
      const newItems = {
        name: newDoc.originalFileName,
        file: null,
        type: newDoc.docType
      };
      setUploads([newItems]);
    }
  }

  return (
    <>
      <Grid container spacing={2} alignItems="stretch" style={{ paddingTop: '0px' }}>
        <Grid item xs={12} style={{ padding: 16, transition: 'all 0.3s ease' }}>
          {documentStore.documentPreviewV2 && uploads[0] &&
            <>
              {isPdfFile ?
                <Box style={{
                  height: '67.9vh',
                  overflowY: 'auto',
                  border: '1px solid #CBCBCB',
                  borderRadius: '10px',
                  padding: '8px',
                  display: showDocumentLoading ? 'flex' : 'block',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'all 0.5s ease',
                }}>
                  {showDocumentLoading ? <CircularProgress size={32} /> :
                    <Document
                      file={documentStore.documentPreviewV2.data}
                      onLoadSuccess={onDocumentLoadSuccess}
                      className={classes.document}
                    >
                      {numPages &&
                        [...Array(numPages)].map((x, i) => {
                          return (
                            <Page
                              className={classes.page}
                              pageNumber={i + 1}
                              scale={scale}
                            />
                          );
                        })}
                    </Document>
                  }
                </Box>
                :
                <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" style={{
                  height: '67.9vh',
                  overflowY: 'auto',
                  border: '1px solid #CBCBCB',
                  borderRadius: '10px',
                  padding: '8px',
                  transition: 'all 0.5s ease',
                }}>
                  <Error style={{ marginBottom: '5px', fontSize: '40px' }}></Error>
                  <Typography variant="h6" align='center'>
                    We are currently unable to show a preview of the document
                  </Typography>
                </Box>
              }
            </>
          }
        </Grid>
      </Grid>
    </>
  );
});
